import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight, Github, Linkedin } from 'lucide-react';
import Logo from './Logo';

const navLinks = [
  { name: 'About', href: '#about' },
  { name: 'Experience', href: '#experience' },
  { name: 'Projects', href: '#projects' },
  { name: 'Skills', href: '#skills' },
  { name: 'Contact', href: '#contact' },
];

const Header = ({ onBookCall }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* Lock body scroll when mobile menu is open */
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const scrollToSection = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass border-b' : 'bg-transparent'
        }`}
        style={{ borderColor: isScrolled ? 'rgba(255,255,255,0.07)' : 'transparent' }}
        data-testid="header"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-[68px]">
            {/* Logo */}
            <motion.a
              href="#"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="flex items-center text-white"
              whileHover={{ scale: 1.04 }}
              data-testid="logo"
              aria-label="Rayhan Patel"
            >
              <Logo className="w-9 h-9" />
            </motion.a>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="py-3 text-[12px] text-text-secondary hover:text-white transition-colors duration-200 underline-animation tracking-widest uppercase"
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </button>
              ))}
            </nav>

            {/* Social links + CTA */}
            <div className="hidden md:flex items-center gap-4 ml-auto mr-3 lg:ml-0 lg:mr-0">
              <div className="flex items-center gap-0.5">
                <motion.a
                  href="https://github.com/rayhanpatel88"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center text-text-secondary hover:text-white transition-colors duration-200"
                  whileHover={{ scale: 1.12, y: -1 }}
                  aria-label="GitHub"
                >
                  <Github className="w-[15px] h-[15px]" />
                </motion.a>
                <motion.a
                  href="https://linkedin.com/in/rayhanpatel1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center text-text-secondary hover:text-white transition-colors duration-200"
                  whileHover={{ scale: 1.12, y: -1 }}
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-[15px] h-[15px]" />
                </motion.a>
              </div>

              {/* Divider */}
              <div className="w-px h-3.5 bg-white/10" />

              <motion.button
                onClick={onBookCall}
                className="group flex items-center gap-2 px-5 py-2.5 bg-white text-black text-[12px] font-medium rounded-full hover:bg-white/90 transition-colors duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                data-testid="header-book-call-btn"
              >
                Book a Call
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </motion.button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden flex h-11 w-11 -mr-2.5 items-center justify-center text-text-secondary hover:text-white transition-colors"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 lg:hidden overflow-y-auto overscroll-contain"
            data-testid="mobile-menu"
          >
            <div
              className="absolute inset-0"
              style={{ background: '#050505' }}
            />
            <motion.nav
              initial={{ y: -12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -12, opacity: 0 }}
              transition={{ delay: 0.05, duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              className="relative pt-24 pb-12 px-6 flex flex-col gap-1"
            >
              {navLinks.map((link, i) => (
                <motion.button
                  key={link.name}
                  initial={{ x: -16, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.08 + i * 0.05, ease: [0.25, 0.1, 0.25, 1] }}
                  onClick={() => scrollToSection(link.href)}
                  className="font-display text-[2.6rem] font-medium text-left py-2 hover:text-text-secondary transition-colors duration-200 leading-tight"
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </motion.button>
              ))}
              <motion.button
                initial={{ x: -16, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.34, ease: [0.25, 0.1, 0.25, 1] }}
                onClick={() => { onBookCall(); setMobileMenuOpen(false); }}
                className="mt-8 w-full py-4 bg-white text-black font-medium rounded-full text-sm"
                data-testid="mobile-book-call-btn"
              >
                Book a Call
              </motion.button>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.45 }}
                className="mt-8 pt-6 border-t"
                style={{ borderColor: 'rgba(255,255,255,0.07)' }}
              >
                <p className="text-text-secondary text-xs tracking-widest uppercase">
                  rayhanpatel88@gmail.com
                </p>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
